import { alertAnatomy as parts } from "@chakra-ui/anatomy"
import { createMultiStyleConfigHelpers, cssVar } from "@chakra-ui/styled-system"

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(parts.keys)

const $fg = cssVar("alert-fg")
const $bg = cssVar("alert-bg")

const baseStyle = definePartsStyle({
  container: {
    minH: "2.8125rem",
    fontFamily: "Manrope Variable, sans-serif",
    fontSize: 14,
  },
  title: {
    fontWeight: 600,
  },
  icon: {
    h: 5,
    py: 0.25,
  },
})

// Chakra's built-in status → colorScheme mapping cannot be overridden via the
// theme — it is hardcoded in the component source. We simply override the styles
// for each mapped color scheme, so consumers only ever have to pass `status`. We
// set CSS variables on the container slot, which are then referenced by the icon
// and spinner slots.
//
// The mapping is as follows:
//   const STATUSES = {
//     info:    { icon: InfoIcon,    colorScheme: "blue"   },
//     warning: { icon: WarningIcon, colorScheme: "orange" },
//     success: { icon: CheckIcon,   colorScheme: "green"  },
//     error:   { icon: WarningIcon, colorScheme: "red"    },
//     loading: { icon: Spinner,     colorScheme: "blue"   },
//   }
const variantSolid = definePartsStyle((props) => {
  const { colorScheme } = props

  if (colorScheme === "green") {
    return {
      container: {
        bg: "main.700",
      },
    }
  }

  if (colorScheme === "blue") {
    return {
      container: {
        [$fg.variable]: "colors.neutrals.850",
        bg: "neutrals.100",
        color: $fg.reference,
      },
    }
  }

  return {}
})

const variantSubtle = definePartsStyle((props) => {
  const { colorScheme } = props

  if (colorScheme === "blue") {
    return {
      container: {
        [$fg.variable]: "colors.white",
        [$bg.variable]: "colors.black",
        color: $fg.reference,
        bg: $bg.reference,
      },
    }
  }

  // `blue` is reserved by Chakra's `info` status, so the light-blue advisory
  // tone (matching the migration step banners) is exposed under its own scheme.
  if (colorScheme === "lightBlue") {
    return {
      container: {
        [$fg.variable]: "colors.blue.700",
        [$bg.variable]: "colors.blue.50",
        color: $fg.reference,
        bg: $bg.reference,
        borderColor: "blue.200",
      },
      icon: {
        color: "blue.500",
      },
    }
  }

  if (colorScheme === "green") {
    return {
      container: {
        [$fg.variable]: "colors.success.800",
        [$bg.variable]: "colors.success.50",
        color: $fg.reference,
        bg: $bg.reference,
        borderColor: "success.200",
      },
    }
  }

  if (colorScheme === "orange") {
    return {
      container: {
        [$fg.variable]: "colors.warning.800",
        [$bg.variable]: "colors.warning.50",
        color: $fg.reference,
        bg: $bg.reference,
        borderColor: "warning.200",
      },
      icon: {
        color: "warning.600",
      },
    }
  }

  if (colorScheme === "red") {
    return {
      container: {
        [$fg.variable]: "colors.error.900",
        [$bg.variable]: "colors.error.50",
        color: $fg.reference,
        bg: $bg.reference,
        borderColor: "error.200",
      },
    }
  }

  if (colorScheme === "purple") {
    return {
      container: {
        [$fg.variable]: "colors.purple.600",
        [$bg.variable]: "colors.purple.50",
        color: $fg.reference,
        bg: $bg.reference,
        borderColor: "purple.200",
      },
      icon: {
        color: "purple.500",
      },
    }
  }

  return {}
})

/**
 * @see {@link https://github.com/chakra-ui/chakra-ui/blob/v2/packages/theme/src/components/alert.ts}
 */
export const Alert = defineMultiStyleConfig({
  baseStyle,
  variants: {
    solid: variantSolid,
    subtle: variantSubtle,
  },
})
